/**
 * Product-module availability flags.
 *
 * Purpose: keep INCOMPLETE areas of the app out of a reviewer's / teacher's
 * path. Anything not yet finished-and-tested is OFF here, so its nav entry is
 * hidden and its route redirects to the dashboard — a reviewer never lands on a
 * stub, dead button, or "coming soon" screen.
 *
 * This is distinct from appConfig.features (operational toggles like autosave).
 * Flip a flag to true — or set the matching REACT_APP_FEATURE_* env var to
 * "true" — once that module is complete. Keep this list honest: a flag should
 * be ON only if a teacher can use the whole feature without hitting a dead end.
 */

/**
 * S84: every one of these must be a STATIC `process.env.X` member expression.
 *
 * This used to be `envFlag('REACT_APP_FEATURE_LMS', true)` — a computed lookup,
 * `process.env[key]`. Vite/esbuild's `define` is a text substitution over static
 * member expressions, so `process.env.REACT_APP_FEATURE_BILLING` is replaced and
 * `process.env[key]` is NOT: it matches only the catch-all `'process.env': '{}'`
 * and compiles to a lookup in an empty object. Every variable therefore read as
 * `undefined` and every flag fell back to its hardcoded `true`.
 *
 * Consequence: `npm run deploy:staging` builds with `--mode staging`, Vite defines
 * all nine variables from `.env.staging`, and the built app ignored all nine. A
 * staging site handed to a district reviewer exposed the LMS integration and the
 * Stripe billing routes that the config file had explicitly turned off — the two
 * modules whose backends are, by this file's own words, not configured.
 *
 * This is the repo's signature shape in build configuration: a read against
 * something nothing writes, silent, with the permissive branch as the default.
 */
const flagFrom = (value: string | undefined, fallback: boolean): boolean => {
  if (value === undefined || value === '') return fallback;
  return value === 'true' || value === '1';
};

export const FEATURE_FLAGS = {
  /** LMS integration (Google Classroom / Canvas) — credential-gated backend. */
  lmsIntegration: flagFrom(process.env.REACT_APP_FEATURE_LMS, true),
  /** Real-time collaboration — wired to collaborationService (chat/roles/leave). */
  realtimeCollaboration: flagFrom(process.env.REACT_APP_FEATURE_COLLABORATION, true),
  /** Student assignments — wired to assignmentService (submit + feedback). */
  studentAssignments: flagFrom(process.env.REACT_APP_FEATURE_STUDENT_ASSIGNMENTS, true),
  /** Family mode — wired to familyModeService (child select, sessions, chat). */
  familyMode: flagFrom(process.env.REACT_APP_FEATURE_FAMILY_MODE, true),
  /** Game jams — wired to gameJamService (real create/join/submit). */
  gameJams: flagFrom(process.env.REACT_APP_FEATURE_GAME_JAMS, true),
  /** Presentation mode — loads the project by id and presents it. */
  presentationMode: flagFrom(process.env.REACT_APP_FEATURE_PRESENTATION, true),
  /** Standalone recording — records the game via screen/tab capture. */
  standaloneRecording: flagFrom(process.env.REACT_APP_FEATURE_RECORDING_PAGE, true),
  /** Billing — real Stripe-extension flow + license mgmt (credential-gated). */
  billing: flagFrom(process.env.REACT_APP_FEATURE_BILLING, true),
  /** Level/leaderboard page — wired to levelingService (real level + leaderboard). */
  levelProgress: flagFrom(process.env.REACT_APP_FEATURE_LEVEL_PAGE, true),
} as const;

export type FeatureFlag = keyof typeof FEATURE_FLAGS;

/**
 * S84: own properties only, and a real boolean.
 *
 * `FEATURE_FLAGS['toString']` walked the prototype chain and returned a FUNCTION,
 * which is truthy — so `<FeatureGate feature="toString">` would render its
 * children rather than redirect. Unreachable while every call site is a string
 * literal typed `FeatureFlag`; it stops being unreachable the moment a flag name
 * arrives from a URL, a Firestore document, or an admin-configurable module list.
 *
 * It also returned `undefined` for an unknown name while its signature promised
 * `boolean`, so `isFeatureEnabled(f) === false` read as "the flag is off" and was
 * FALSE for a typo. An unknown flag is OFF — the restrictive answer.
 */
export const isFeatureEnabled = (flag: FeatureFlag): boolean =>
  Object.prototype.hasOwnProperty.call(FEATURE_FLAGS, flag)
    ? FEATURE_FLAGS[flag] === true
    : false;
